<template>
  <div class="product-management-container">
    <div class="page-header">
      <div class="blue-bar"></div>
      <h2 class="page-title">产品管理</h2>
    </div>

    <!-- 搜索区域 -->
    <div class="search-area">
      <div class="search-inputs">
        <div class="search-item">
          <el-input
            v-model="searchForm.productName"
            placeholder="产品名称"
            prefix-icon="el-icon-search"
            clearable
          ></el-input>
        </div>
        <div class="search-item">
          <el-input
            v-model="searchForm.specification"
            placeholder="规格型号"
            clearable
          ></el-input>
        </div>
        <div class="search-buttons">
          <el-button type="primary" @click="handleSearch" icon="el-icon-search">搜索</el-button>
          <el-button type="success" @click="showAddDialog" icon="el-icon-plus">录入产品</el-button>
          <el-button type="primary" @click="showImportSection" icon="el-icon-upload2">导入产品</el-button>
        </div>
      </div>
    </div>

    <!-- 导入区域（可折叠） -->
    <div v-show="importSectionVisible" class="import-section">
      <el-alert
        title="导入说明"
        description="请先下载产品模板，按模板格式填写后导入。模板包含：序号、产品名称、规格型号、材质要求、单位、产品数量、成本价(元)、备注"
        type="info"
        :closable="false"
        show-icon
      ></el-alert>
      <div class="import-actions">
        <el-button v-permission="'basic:basic-products:download-template'" type="info" icon="el-icon-download" @click="downloadTemplate">下载产品模板</el-button>
        <el-upload
          class="excel-uploader"
          action="#"
          :show-file-list="false"
          :before-upload="beforeExcelUpload"
          :http-request="handleExcelUpload"
        >
          <el-button type="primary" icon="el-icon-upload2">导入Excel</el-button>
        </el-upload>
      </div>
      <p class="upload-tip">支持格式：.xlsx、.xls、.csv（建议 UTF-8 编码），文件大小不超过10MB</p>
    </div>

    <!-- 表格区域 -->
    <div class="table-container">
      <el-table
        :data="tableData"
        border
        stripe
        style="width: 100%"
        v-loading="loading"
        :header-cell-style="{ background: '#f5f7fa', color: '#606266' }"
      >
        <el-table-column label="序号" prop="sequenceNumber" width="80" align="center"></el-table-column>
        <el-table-column prop="productName" label="产品名称" min-width="160" align="center"></el-table-column>
        <el-table-column prop="specification" label="规格型号" min-width="150" align="center">
          <template slot-scope="scope">
            <el-tooltip
              v-if="scope.row.specification"
              :content="scope.row.specification"
              placement="top"
              effect="dark"
            >
              <span>{{ truncateText(scope.row.specification, 20) }}</span>
            </el-tooltip>
            <span v-else>-</span>
          </template>
        </el-table-column>
        <el-table-column prop="materialRequirement" label="材质要求" min-width="120" align="center"></el-table-column>
        <el-table-column prop="unit" label="单位" width="80" align="center"></el-table-column>
        <el-table-column prop="quantity" label="产品数量" width="100" align="center">
          <template slot-scope="scope">
            {{ scope.row.quantity != null && scope.row.quantity !== '' ? scope.row.quantity : 0 }}
          </template>
        </el-table-column>
        <el-table-column prop="costPrice" label="成本价(元)" width="120" align="center">
          <template slot-scope="scope">
            {{ formatAmount(scope.row.costPrice) }}
          </template>
        </el-table-column>
        <el-table-column prop="remark" label="备注" min-width="150" align="center"></el-table-column>
        <el-table-column label="操作" width="120" fixed="right" align="center">
          <template slot-scope="scope">
            <el-button type="text" size="small" @click="handleEdit(scope.row)">编辑</el-button>
            <el-button type="text" size="small" class="delete-btn" @click="handleDelete(scope.row)">删除</el-button>
          </template>
        </el-table-column>
      </el-table>

      <div class="pagination-container">
        <el-pagination
          @size-change="handleSizeChange"
          @current-change="handleCurrentChange"
          :current-page="pagination.currentPage"
          :page-sizes="[10, 20, 30, 50]"
          :page-size="pagination.pageSize"
          layout="prev, pager, next, jumper, sizes, total"
          :total="pagination.total"
        >
        </el-pagination>
      </div>
    </div>

    <!-- 新增/编辑对话框 -->
    <el-dialog
      :title="dialogTitle"
      :visible.sync="dialogVisible"
      width="700px"
      :close-on-click-modal="false"
      @close="closeDialog"
    >
      <el-form ref="productForm" :model="productForm" :rules="rules" label-width="120px">
        <el-row :gutter="20">
          <el-col :span="12">
            <el-form-item label="产品名称" prop="productName">
              <el-input v-model="productForm.productName" placeholder="请输入产品名称"></el-input>
            </el-form-item>
          </el-col>
          <el-col :span="12">
            <el-form-item label="规格型号" prop="specification">
              <el-input v-model="productForm.specification" placeholder="请输入规格型号"></el-input>
            </el-form-item>
          </el-col>
        </el-row>
        <el-row :gutter="20">
          <el-col :span="12">
            <el-form-item label="材质要求" prop="materialRequirement">
              <el-input v-model="productForm.materialRequirement" placeholder="请输入材质要求"></el-input>
            </el-form-item>
          </el-col>
          <el-col :span="12">
            <el-form-item label="单位" prop="unit">
              <el-input v-model="productForm.unit" placeholder="如：吨、米、个"></el-input>
            </el-form-item>
          </el-col>
        </el-row>
        <el-row :gutter="20">
          <el-col :span="12">
            <el-form-item label="产品数量" prop="quantity">
              <el-input-number
                v-model="productForm.quantity"
                :min="0"
                :precision="2"
                placeholder="数量"
                style="width: 100%;"
              ></el-input-number>
            </el-form-item>
          </el-col>
          <el-col :span="12">
            <el-form-item label="成本价(元)" prop="costPrice">
              <el-input-number
                v-model="productForm.costPrice"
                :min="0"
                :precision="4"
                placeholder="成本价"
                style="width: 100%;"
              ></el-input-number>
            </el-form-item>
          </el-col>
        </el-row>
        <el-form-item label="备注" prop="remark">
          <el-input v-model="productForm.remark" type="textarea" :rows="3" placeholder="请输入备注"></el-input>
        </el-form-item>
      </el-form>
      <div slot="footer" class="dialog-footer">
        <el-button @click="dialogVisible = false">取消</el-button>
        <el-button type="primary" @click="submitForm">保存</el-button>
      </div>
    </el-dialog>

    <!-- 删除确认 -->
    <el-dialog title="提示" :visible.sync="deleteDialogVisible" width="400px" center :close-on-click-modal="false">
      <div class="delete-dialog-content">
        <i class="el-icon-warning-outline warning-icon"></i>
        <span>确认删除该产品吗？</span>
      </div>
      <div slot="footer" class="dialog-footer">
        <el-button @click="deleteDialogVisible = false">取消</el-button>
        <el-button type="primary" @click="confirmDelete">确定</el-button>
      </div>
    </el-dialog>
  </div>
</template>

<script>
import * as XLSX from 'xlsx'
import { getUserInfo } from '@/utils/auth'
import {
  queryProductsPagination,
  saveProduct,
  deleteProduct,
  importProducts
} from '@/api/modules/product.js'

export default {
  name: 'ProductManagement',
  data() {
    return {
      searchForm: {
        productName: '',
        specification: ''
      },
      loading: false,
      tableData: [],
      pagination: {
        currentPage: 1,
        pageSize: 10,
        total: 0
      },
      currentUnitId: null,
      importSectionVisible: false,
      dialogVisible: false,
      deleteDialogVisible: false,
      isEdit: false,
      deleteId: null,
      productForm: {
        id: null,
        productName: '',
        specification: '',
        materialRequirement: '',
        unit: '',
        quantity: 0,
        costPrice: 0,
        remark: ''
      },
      rules: {
        productName: [{ required: true, message: '请输入产品名称', trigger: 'blur' }]
      }
    }
  },
  computed: {
    dialogTitle() {
      return this.isEdit ? '编辑产品' : '新增产品'
    }
  },
  created() {
    this.getCurrentUnitId()
  },
  methods: {
    getCurrentUnitId() {
      try {
        const userInfo = getUserInfo() || {}
        this.currentUnitId = userInfo?.unitId || null
        this.fetchProductList()
      } catch (err) {
        console.error('获取单位ID失败:', err)
        this.$message.error('获取单位信息失败')
      }
    },

    fetchProductList() {
      this.loading = true
      const params = {
        page: this.pagination.currentPage - 1,
        size: this.pagination.pageSize,
        productName: this.searchForm.productName,
        specification: this.searchForm.specification
      }
      queryProductsPagination(this.currentUnitId, params)
        .then(res => {
          const data = res.data?.data
          if (data) {
            this.tableData = data.content || []
            this.pagination.total = data.totalElements || 0
          }
        })
        .catch(err => {
          console.error('查询产品列表失败:', err)
          this.$message.error('查询失败，请稍后重试')
        })
        .finally(() => {
          this.loading = false
        })
    },

    handleSearch() {
      this.pagination.currentPage = 1
      this.fetchProductList()
    },

    showAddDialog() {
      this.isEdit = false
      this.resetForm()
      this.dialogVisible = true
    },

    showImportSection() {
      this.importSectionVisible = !this.importSectionVisible
    },

    handleEdit(row) {
      this.isEdit = true
      this.productForm = {
        id: row.id,
        productName: row.productName,
        specification: row.specification,
        materialRequirement: row.materialRequirement,
        unit: row.unit,
        quantity: row.quantity,
        costPrice: row.costPrice,
        remark: row.remark
      }
      this.dialogVisible = true
    },

    handleDelete(row) {
      this.deleteId = row.id
      this.deleteDialogVisible = true
    },

    confirmDelete() {
      if (!this.deleteId) return
      this.loading = true
      deleteProduct(this.currentUnitId, this.deleteId)
        .then(res => {
          if (res.data?.success) {
            this.$message.success('删除成功')
            this.deleteDialogVisible = false
            this.fetchProductList()
          } else {
            this.$message.error(res.data?.message || '删除失败')
          }
        })
        .catch(err => {
          console.error('删除失败:', err)
          this.$message.error('删除失败，请稍后重试')
        })
        .finally(() => {
          this.loading = false
        })
    },

    closeDialog() {
      this.resetForm()
    },

    resetForm() {
      this.productForm = {
        id: null,
        productName: '',
        specification: '',
        materialRequirement: '',
        unit: '',
        quantity: 0,
        costPrice: 0,
        remark: ''
      }
      this.$refs.productForm && this.$refs.productForm.resetFields()
    },

    submitForm() {
      this.$refs.productForm.validate(valid => {
        if (!valid) return
        this.loading = true
        saveProduct(this.currentUnitId, this.productForm)
          .then(res => {
            if (res.data?.success) {
              this.$message.success('保存成功')
              this.dialogVisible = false
              this.fetchProductList()
            } else {
              this.$message.error(res.data?.message || '保存失败')
            }
          })
          .catch(err => {
            console.error('保存失败:', err)
            this.$message.error('保存失败，请稍后重试')
          })
          .finally(() => {
            this.loading = false
          })
      })
    },

    beforeExcelUpload(file) {
      const isExcelOrCsv =
        file.type === 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' ||
        file.type === 'application/vnd.ms-excel' ||
        file.type === 'text/csv' ||
        file.name.endsWith('.xlsx') ||
        file.name.endsWith('.xls') ||
        file.name.endsWith('.csv')
      if (!isExcelOrCsv) {
        this.$message.error('只能上传 Excel (.xlsx/.xls) 或 CSV 文件!')
        return false
      }
      if (file.size / 1024 / 1024 > 10) {
        this.$message.error('文件大小不能超过10MB!')
        return false
      }
      return true
    },

    handleExcelUpload({ file }) {
      const loading = this.$loading({
        lock: true,
        text: '正在导入...',
        spinner: 'el-icon-loading',
        background: 'rgba(0, 0, 0, 0.7)'
      })
      importProducts(file)
        .then(res => {
          const data = res.data
          if (data?.success) {
            const msg = data.message || (data.data ? `成功导入 ${data.data.successCount || 0} 条` : '导入成功')
            this.$message.success(msg)
            this.importSectionVisible = false
            this.fetchProductList()
          } else {
            this.$message.error(data?.message || '导入失败')
          }
        })
        .catch(err => {
          console.error('导入失败:', err)
          const msg = err?.response?.data?.message || err?.message || '导入失败，请稍后重试'
          this.$message.error(msg)
        })
        .finally(() => {
          loading.close()
        })
    },

    downloadTemplate() {
      const headers = ['序号', '产品名称', '规格型号', '材质要求', '单位', '产品数量', '成本价(元)', '备注']
      const sample = [
        [1, '示例产品1', '规格A', '材质要求', '吨', 100, 100.5, ''],
        [2, '示例产品2', '规格B', '', '米', 50, 50, '备注']
      ]
      const wsData = [headers, ...sample]
      const ws = XLSX.utils.aoa_to_sheet(wsData)
      const wb = XLSX.utils.book_new()
      XLSX.utils.book_append_sheet(wb, ws, '产品清单')
      XLSX.writeFile(wb, '产品清单模板.xlsx')
      this.$message.success('正在下载产品清单模板')
    },

    handleSizeChange(val) {
      this.pagination.pageSize = val
      this.fetchProductList()
    },

    handleCurrentChange(val) {
      this.pagination.currentPage = val
      this.fetchProductList()
    },

    truncateText(text, len = 20) {
      if (!text) return '-'
      const s = String(text)
      return s.length > len ? s.slice(0, len) + '...' : s
    },

    formatAmount(amount) {
      if (amount == null || amount === '') return '0.0000'
      const num = parseFloat(amount)
      return isNaN(num) ? '0.0000' : num.toLocaleString('zh-CN', { minimumFractionDigits: 4, maximumFractionDigits: 4 })
    }
  }
}
</script>

<style lang="scss" scoped>
.product-management-container {
  background-color: #fff;
  padding: 20px;
  min-height: calc(100vh - 120px);
  border-radius: 4px;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.05);

  .page-header {
    display: flex;
    align-items: center;
    margin-bottom: 20px;

    .blue-bar {
      width: 3px;
      background-color: #1890ff;
      height: 16px;
      margin-right: 8px;
    }

    .page-title {
      font-size: 16px;
      font-weight: 500;
      color: #333;
      margin: 0;
    }
  }

  .search-area {
    margin-bottom: 20px;

    .search-inputs {
      display: flex;
      flex-wrap: wrap;

      .search-item {
        margin-right: 15px;
        margin-bottom: 10px;
        width: 200px;
      }

      .search-buttons .el-button {
        margin-right: 10px;
      }
    }
  }

  .import-section {
    margin-bottom: 20px;
    padding: 20px;
    background: #f8f9fc;
    border-radius: 8px;
    border: 1px solid #e8eaed;

    .el-alert {
      margin-bottom: 4px;
    }

    .import-actions {
      display: flex;
      align-items: center;
      flex-wrap: wrap;
      gap: 16px;
      margin: 16px 0 12px;

      .excel-uploader {
        display: inline-block;

        ::v-deep .el-upload {
          display: inline-block;
        }
      }

      .el-button {
        margin: 0;
      }
    }

    .upload-tip {
      margin: 0;
      padding-top: 4px;
      font-size: 12px;
      color: #909399;
      line-height: 1.5;
    }
  }

  .table-container {
    .delete-btn {
      color: #f56c6c;
    }

    .pagination-container {
      display: flex;
      justify-content: center;
      margin-top: 20px;
    }
  }

  .delete-dialog-content {
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 30px 0;

    .warning-icon {
      font-size: 24px;
      color: #e6a23c;
      margin-right: 10px;
    }

    span {
      font-size: 16px;
    }
  }
}
</style>
